<template>
  <BaseBulkMessageListItem
    :is-current="isCurrent"
    v-loading="isBusy"
  >
    <template v-slot:badges>
      <Badge type="sms"></Badge>
      <Badge v-if="isDraft" type="draft"></Badge>
      <Badge v-else-if="isQueued" type="queued" :text="badgeQueuedText"></Badge>
      <Badge v-else-if="isScheduled" type="scheduled" :text="badgeSchedulesText"></Badge>
      <Badge v-else-if="isSent" type="sent" :text="badgeSentText"></Badge>
      <Badge v-else-if="isError" type="error"></Badge>
      <Badge v-else-if="isCanceled" type="canceled"></Badge>
    </template>
    <template v-slot:menu>
      <div v-if="isSent || isCanceled" @click="showMessages" class="dropdown-item">Show messages</div>
      <div v-if="isScheduled" @click="showMessagesPreview" class="dropdown-item">Preview messages</div>
      <div v-if="isCancelable && canUpdate" @click="cancelSending" class="dropdown-item">Cancel</div>
    </template>
    <template v-slot:body>
      <div>{{body}}</div>
    </template>
  </BaseBulkMessageListItem>
</template>

<script>
import BaseBulkMessageListItem from "./base-bulk-message-list-item.vue";
import Badge from "./badge.vue";

export default {
  components: {
    Badge,
    BaseBulkMessageListItem
  },

  props: {
    item: null,
    isCurrent: null,
  },

  data() {
    return {
      isBusy: false,
    };
  },

  computed: {
    /**
     * @return {Object}
     */
    bulkMessage() {
      return this.item.bulk_message;
    },

    /**
     * @return {string}
     */
    body() {
      return _.get(this.bulkMessage, 'body', '');
    },

    /**
     * @return {boolean}
     */
    isDraft() {
      return this.bulkMessage.status === 1;
    },

    /**
     * @return {boolean}
     */
    isQueued() {
      return this.bulkMessage.status === 2 && _.includes([1, 3], this.bulkMessage.state);
    },

    /**
     * @return {boolean}
     */
    isScheduled() {
      return this.bulkMessage.status === 2 && this.bulkMessage.state === 2;
    },

    /**
     * @return {boolean}
     */
    isSent() {
      return this.bulkMessage.status === 2 && this.bulkMessage.state === 4;
    },

    /**
     * @return {boolean}
     */
    isError() {
      return this.bulkMessage.status === 2 && this.bulkMessage.state === 5;
    },

    /**
     * @return {boolean}
     */
    isCanceled() {
      return this.bulkMessage.status === 3;
    },

    /**
     * @return {string|null}
     */
    formattedStartAt() {
      const value = _.get(this.item, 'start_at.datetime');

      return value ? this.formatDate(value) : null;
    },

    /**
     * @return {string|null}
     */
    formattedSentAt() {
      const value = _.get(this.bulkMessage, 'sent_at.datetime');

      return value ? this.formatDate(value) : null;
    },

    /**
     * @return {string}
     */
    badgeQueuedText() {
      return 'QUEUED' + (this.formattedStartAt ? `: ${this.formattedStartAt}` : '');
    },

    /**
     * @return {string}
     */
    badgeSchedulesText() {
      return 'SCHEDULED' + (this.formattedStartAt ? `: start at ${this.formattedStartAt}` : '');
    },

    /**
     * @return {string}
     */
    badgeSentText() {
      return 'SENT' + (this.formattedSentAt ? `: ${this.formattedSentAt}` : '');
    },

    /**
     * @return {boolean}
     */
    canUpdate() {
      return _.get(this.bulkMessage, 'canUpdate') === true;
    },

    /**
     * @return {boolean}
     */
    isCancelable() {
      return this.bulkMessage.isCancelable;
    },
  },

  methods: {
    /**
     * @param date
     * @return {string}
     */
    formatDate(date) {
      return moment(date).format(config('format.moment.short.datetime'));
    },

    /**
     * @emits show-messages
     */
    showMessages() {
      this.$emit('show-messages', {type: 'sms-message-list', item: this.item});
    },

    /**
     * @emits show-messages
     */
    showMessagesPreview() {
      this.$emit('show-messages', {type: 'sms-message-preview-list', item: this.item});
    },

    /**
     * @return {Promise}
     */
    cancelSending() {
      this.isBusy = true;
      const url = _.get(this.bulkMessage, 'cancelUrl');

      return axios.post(url)
        .then(({data: {message}}) => {
          _.isString(message) && this.$message.success(message);
          this.$emit('refresh-list');
        })
        .catch(this.handleErrorResponse)
        .then(() => this.isBusy = false);
    },

    /**
     * @param data
     * @param status
     * @return {boolean}
     */
    handleErrorResponse({response: {data, status}}) {
      const {errors, message} = data || {};
      if (status === 422 && errors) {
        this.$message.error(message);
      } else if (message && _.isString(message)) {
        this.$message.error(message);
      }
    },
  },
}
</script>
